import type { ReactNode } from "react"
import { modifiers } from "ui-tools"
import {
  ConversationList,
  type ConversationRowModel,
} from "../ConversationList"
import type { Jid } from "../../xmpp/jid"
import { EmptyThread } from "./EmptyThread"
import s from "./MessagesView.module.scss"

type MessagesViewProps = {
  conversations: ConversationRowModel[]
  /** The open conversation, from the route. Undefined means none. */
  activePeer?: Jid
  /** The open conversation's thread. Ignored when nothing is open. */
  thread?: ReactNode
  /** The new-chat modal, when it is open. Rendered over both panes. */
  newChat?: ReactNode
  /** The group dialog the menu opened, if any. */
  groupDialog?: ReactNode
  /** The share-from-Attensi picker, when it is open. */
  sharePicker?: ReactNode
  /** A connection notice, above both panes so it shows on the list too. */
  banner?: ReactNode
  /** Pinned to the foot of the list — the notification permission prompt. */
  listFooter?: ReactNode
  onOpen: (peer: Jid) => void
  onNewMessage: () => void
}

export function MessagesView({
  conversations,
  activePeer,
  thread,
  newChat,
  groupDialog,
  sharePicker,
  banner,
  listFooter,
  onOpen,
  onNewMessage,
}: MessagesViewProps) {
  const isThreadOpen = activePeer !== undefined

  return (
    <div className={s.messages} data-messages-fullbleed>
      {/* Spans both columns, above the panes; the row collapses when empty. */}
      <div className={s.messages__banner}>{banner}</div>

      {/* Both panes stay mounted: below the small breakpoint one is hidden
          rather than unmounted, so the list keeps its scroll position. */}
      <div
        className={modifiers(s, "messages__list", { isHidden: isThreadOpen })}
      >
        <ConversationList
          conversations={conversations}
          activePeer={activePeer}
          onOpen={onOpen}
          onNewMessage={onNewMessage}
          footer={listFooter}
        />
      </div>

      <div
        className={modifiers(s, "messages__thread", {
          isHidden: !isThreadOpen,
        })}
      >
        {thread ?? <EmptyThread onNewMessage={onNewMessage} />}
      </div>

      {newChat}
      {groupDialog}
      {sharePicker}
    </div>
  )
}
